import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { API_PATHS } from '../config/api-paths';
import { GENERAL_CHANNEL, LenderChannelOption, LenderChannelsApi } from '../tokens/lender-channels.token';

/**
 * The General channel, used only when the backend gave nothing. The label mirrors the server's so a
 * transport hiccup degrades the scope bar to "General only" instead of blanking it — never a
 * fabricated partner (Rule 10).
 */
const GENERAL_FALLBACK: LenderChannelOption = {
  channel: GENERAL_CHANNEL,
  name: 'Lendox Marketplace (General)',
  edId: null,
  general: true,
};

/**
 * How long a successful catalogue is reused. Long enough that one screen load issues a single GET,
 * short enough that a partner onboarded mid-session appears without a hard reload.
 */
export const CATALOGUE_TTL_MS = 60_000;

/**
 * REST-only implementation of {@link LenderChannelsApi} (EM-1613).
 *
 * The **single** source of the lender's configuration-scope channels for the whole portal — the
 * dashboard Exposure-by-E-D widget, Partner Space pills, the risk / pricing / exposure scope bars,
 * the portfolio E-D filter and the Invited-SMEs grouping all consume this instead of each keeping
 * their own copy of the removed `marketplace | metro | selgros | arabesque` demo list (Rule 17).
 *
 * Calls:
 *   GET /services/embedded/api/v1/lender/channels → {@link LenderChannelOption}[]
 *
 * A **successful** response is cached for {@link CATALOGUE_TTL_MS}, not for the session: every screen
 * in the shell asks for it (a dashboard load alone needs it once for the widget and once per exposure
 * limit), so an unbounded cache would be wrong in the other direction — the backend deliberately does
 * not cache precisely because a partner can be onboarded, or submit its first proposal, while the SM
 * has the portal open. A stale catalogue would leave that partner's pill missing AND fold its exposure
 * into the General bucket (the dashboard buckets an unknown `edId` there), which is the very defect
 * EM-1613 exists to remove. A short TTL keeps the request count sane without outliving the data.
 *
 * A failure is **not** cached at all — the caller still gets a usable catalogue (the General channel
 * alone) so the scope bar renders, but the next screen retries rather than being stuck on a degraded
 * list for the whole session. Rides the Keycloak bearer via `authInterceptor`.
 */
@Injectable()
export class LenderChannelsRestService implements LenderChannelsApi {
  private readonly http = inject(HttpClient);

  /** The last successful catalogue; `null` until one arrives or once it has gone stale. */
  private cached: LenderChannelOption[] | null = null;
  /** When {@link cached} was stored, as `Date.now()`. */
  private cachedAt = 0;
  /** The in-flight request, shared so concurrent screens issue a single GET. */
  private inFlight: Observable<LenderChannelOption[]> | null = null;

  getChannels(): Observable<LenderChannelOption[]> {
    if (this.cached && Date.now() - this.cachedAt < CATALOGUE_TTL_MS) {
      return of(this.cached);
    }
    if (!this.inFlight) {
      this.inFlight = this.http.get<LenderChannelOption[]>(API_PATHS.LENDER_CHANNELS).pipe(
        map(rows => (rows ?? []).filter(r => !!r?.channel)),
        map(rows => (rows.length > 0 ? rows : [GENERAL_FALLBACK])),
        tap(rows => {
          this.cached = rows;
          this.cachedAt = Date.now();
          this.inFlight = null;
        }),
        catchError(() => {
          this.inFlight = null;
          return of<LenderChannelOption[]>([GENERAL_FALLBACK]);
        }),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
    }
    return this.inFlight;
  }
}
